// Internal Imports
import { GRID } from "../../constants";
import {
  calculateBasePrice,
  calculateConversionFactors,
  calculateEndDate,
  calculateInterMediateValue,
  checkIsDIYPricing,
  isBillingFrequencyEditable,
  isCellEditable,
  isExtBasePriceEditable,
  isPriceEditable,
  isQtyEditable,
  isSellingTermEditable,
  isSkuQtyEditable,
  preCalculateDiscountedPrice,
  preCalculateEndDate,
  preCalculateExtBasePrice,
  preCalculateNetPrice,
  preCalculateQty,
  preCalculateSkuQty,
  preCalculateStartDate,
  updateBasePrice,
  updateBundleQty,
  updateDiscountAmount,
  updateDiscountPrice,
  updateEndDate,
  updateExtBasePrice,
  updateNetPrice,
  updateQty,
} from "../methods";

// Destructuring
const { DEALTABLE_COLUMNS } = GRID;

// Declarations
const LOOKUP = "LOOKUP";
const FUNCTION = "FUNCTION";
const DEFAULT = "DEFAULT";

// * IMP :: DO NOT CHANGE THE KEY SEQUENCE ORDER
export const REACTION_ON_ADDING_PRODUCTS_INTO_DEALTABLE = {
  [DEALTABLE_COLUMNS.PRODUCT]: {
    properties: {
      type: LOOKUP,
      lookUp: "product.id",
    },
  },
  [DEALTABLE_COLUMNS.PRODUCT_NAME]: {
    properties: {
      type: LOOKUP,
      lookUp: "product.name",
    },
  },
  [DEALTABLE_COLUMNS.PRODUCT_CODE]: {
    properties: {
      type: LOOKUP,
      lookUp: "product.code",
    },
  },
  [DEALTABLE_COLUMNS.PRODUCT_DESC]: {
    properties: {
      type: LOOKUP,
      lookUp: "product.description",
    },
  },
  [DEALTABLE_COLUMNS.SKU]: {
    properties: {
      type: LOOKUP,
      lookUp: "product.sku",
    },
  },
  [DEALTABLE_COLUMNS.RECURRING_PERIOD]: {
    properties: {
      type: LOOKUP,
      lookUp: "recurring_period",
    },
  },
  [DEALTABLE_COLUMNS.CHARGE_TYPE]: {
    properties: {
      type: LOOKUP,
      lookUp: "charge_type",
    },
  },
  [DEALTABLE_COLUMNS.UOM]: {
    properties: {
      type: LOOKUP,
      lookUp: "uom",
    },
  },
  [DEALTABLE_COLUMNS.SELLING_TERM]: {
    properties: {
      type: LOOKUP,
      lookUp: "selling_term",
      defaultValue: 1,
    },
  },
  [DEALTABLE_COLUMNS.CHARGE_FREQUENCY]: {
    properties: {
      type: LOOKUP,
      lookUp: "frequency",
      defaultValue: "",
    },
  },
  [DEALTABLE_COLUMNS.QUANTITY_UOM]: {
    properties: {
      type: LOOKUP,
      lookUp: "quantity_uom",
    },
  },
  [DEALTABLE_COLUMNS.ADDITIONAL_DISCOUNT_PERCENT]: {
    properties: {
      type: LOOKUP,
      defaultValue: 0,
    },
  },
  [DEALTABLE_COLUMNS.BILLING_FREQUENCY]: {
    properties: {
      type: LOOKUP,
      lookUp: "billing_frequency",
      defaultValue: "",
    },
  },
  [DEALTABLE_COLUMNS.MAX_USAGE]: {
    properties: {
      type: LOOKUP,
      lookUp: "max_usage",
      defaultValue: 0,
    },
  },
  [DEALTABLE_COLUMNS.MAX_USAGE_UOM]: {
    properties: {
      type: LOOKUP,
      lookUp: "max_usage_uom",
      defaultValue: "",
    },
  },
  [DEALTABLE_COLUMNS.REMARKS]: {
    properties: {
      type: LOOKUP,
      lookUp: "remarks",
      defaultValue: "",
    },
  },
  [DEALTABLE_COLUMNS.MIN_QTY]: {
    properties: {
      type: LOOKUP,
      lookUp: "min_qty",
      defaultValue: 0,
    },
  },
  [DEALTABLE_COLUMNS.PRICELIST_ITEM]: {
    properties: {
      type: LOOKUP,
      lookUp: "id",
    },
  },
  [DEALTABLE_COLUMNS.PTYPE]: {
    properties: {
      type: LOOKUP,
      lookUp: "product.type",
    },
  },
  [DEALTABLE_COLUMNS.COMPUTED_FAMILY]: {
    properties: {
      type: LOOKUP,
      lookUp: "product.computed_family",
    },
  },
  [DEALTABLE_COLUMNS.QUANTITY_EDITABLE]: {
    properties: {
      type: LOOKUP,
      lookUp: "qty_editable",
    },
  },
  [DEALTABLE_COLUMNS.IS_SELLINGTERM_EDITABLE]: {
    properties: {
      type: LOOKUP,
      lookUp: "sellingterm_editable",
    },
  },
  [DEALTABLE_COLUMNS.SKU_QUANTITY_EDITABLE]: {
    properties: {
      type: LOOKUP,
      lookUp: "sku_quantity_editable",
    },
  },
  [DEALTABLE_COLUMNS.BILLING_FREQUENCY_EDITABLE]: {
    properties: {
      type: LOOKUP,
      lookUp: "billing_frequency_editable",
    },
  },
  [DEALTABLE_COLUMNS.SKU_QUANTITY]: {
    properties: {
      type: FUNCTION,
      lookUp: "default_qty",
      defaultValue: 1,
      methods: [preCalculateSkuQty],
    },
  },
  [DEALTABLE_COLUMNS.QUANTITY]: {
    properties: {
      type: FUNCTION,
      lookUp: "default_qty",
      defaultValue: 1,
      methods: [preCalculateQty],
    },
  },
  [DEALTABLE_COLUMNS.EXT_BASE_PRICE]: {
    properties: {
      type: FUNCTION,
      methods: [
        calculateConversionFactors,
        calculateInterMediateValue,
        preCalculateExtBasePrice,
      ],
    },
  },
  [DEALTABLE_COLUMNS.PRICE]: {
    properties: {
      type: FUNCTION,
      methods: [calculateBasePrice],
    },
  },
  [DEALTABLE_COLUMNS.DISCOUNTED_PRICE]: {
    properties: {
      type: FUNCTION,
      methods: [preCalculateDiscountedPrice],
    },
  },
  [DEALTABLE_COLUMNS.NET_PRICE]: {
    properties: {
      type: FUNCTION,
      methods: [preCalculateNetPrice],
    },
  },
  [DEALTABLE_COLUMNS.START_DATE]: {
    properties: {
      type: FUNCTION,
      methods: [preCalculateStartDate],
    },
  },
  [DEALTABLE_COLUMNS.END_DATE]: {
    properties: {
      type: FUNCTION,
      methods: [preCalculateEndDate],
    },
  },
  [DEALTABLE_COLUMNS.DISC_PERCENT]: {
    properties: {
      type: DEFAULT,
      defaultValue: 0,
    },
  },
  [DEALTABLE_COLUMNS.DISCOUNT_AMT]: {
    properties: {
      type: DEFAULT,
      defaultValue: 0,
    },
  },
  [DEALTABLE_COLUMNS.MIN_SELLING_TERM]: {
    properties: {
      type: DEFAULT,
      defaultValue: 0,
    },
  },
  [DEALTABLE_COLUMNS.BUNDLE_PRICING]: {
    properties: {
      type: DEFAULT,
      defaultValue: "",
    },
  },
};

export const HANDLE_CELL_EDITABILITY = {
  [DEALTABLE_COLUMNS.BILLING_FREQUENCY]: {
    method: isBillingFrequencyEditable,
  },
  [DEALTABLE_COLUMNS.EXT_BASE_PRICE]: {
    method: isExtBasePriceEditable,
  },
  [DEALTABLE_COLUMNS.PRICE]: {
    method: isPriceEditable,
  },
  [DEALTABLE_COLUMNS.QUANTITY]: {
    method: isQtyEditable,
  },
  [DEALTABLE_COLUMNS.SELLING_TERM]: {
    method: isSellingTermEditable,
  },
  [DEALTABLE_COLUMNS.SKU_QUANTITY]: {
    method: isSkuQtyEditable,
  },
  [DEALTABLE_COLUMNS.DISC_PERCENT]: {
    method: isCellEditable,
  },
  [DEALTABLE_COLUMNS.ADDITIONAL_DISCOUNT_PERCENT]: {
    method: isCellEditable,
  },
  [DEALTABLE_COLUMNS.START_DATE]: {
    method: isCellEditable,
  },
  [DEALTABLE_COLUMNS.END_DATE]: {
    method: isCellEditable,
  },
};

export const REACTION_ON_CELL_CHANGE = {
  [DEALTABLE_COLUMNS.ADDITIONAL_DISCOUNT_PERCENT]: {
    methods: [updateNetPrice],
  },
  [DEALTABLE_COLUMNS.DISC_PERCENT]: {
    methods: [updateDiscountAmount, updateDiscountPrice, updateNetPrice],
  },
  [DEALTABLE_COLUMNS.EXT_BASE_PRICE]: {
    methods: [updateDiscountAmount, updateDiscountPrice, updateNetPrice],
  },
  [DEALTABLE_COLUMNS.QUANTITY]: {
    methods: [
      checkIsDIYPricing,
      calculateConversionFactors,
      updateQty,
      calculateInterMediateValue,
      updateExtBasePrice,
      updateBasePrice,
      updateDiscountAmount,
      updateDiscountPrice,
      updateNetPrice,
      updateBundleQty,
    ],
  },
  [DEALTABLE_COLUMNS.SELLING_TERM]: {
    methods: [
      calculateConversionFactors,
      calculateEndDate,
      updateEndDate,
      checkIsDIYPricing,
      calculateInterMediateValue,
      updateExtBasePrice,
      updateBasePrice,
      updateDiscountAmount,
      updateDiscountPrice,
      updateNetPrice,
    ],
  },
  [DEALTABLE_COLUMNS.SKU_QUANTITY]: {
    methods: [
      calculateConversionFactors,
      checkIsDIYPricing,
      calculateInterMediateValue,
      updateExtBasePrice,
      updateBasePrice,
      updateDiscountAmount,
      updateDiscountPrice,
      updateNetPrice,
    ],
  },
  [DEALTABLE_COLUMNS.START_DATE]: {
    methods: [updateEndDate],
  },
};

/**
 [DEALTABLE_COLUMNS.ADDITIONAL_DISCOUNT_PERCENT]: {},
  [DEALTABLE_COLUMNS.BILLING_FREQUENCY]: {},
  [DEALTABLE_COLUMNS.BILLING_FREQUENCY_EDITABLE]: {},
  [DEALTABLE_COLUMNS.BUNDLE_QUANTITY]: {},
  [DEALTABLE_COLUMNS.CHARGE_FREQUENCY]: {},
  [DEALTABLE_COLUMNS.CHARGE_TYPE]: {},
  [DEALTABLE_COLUMNS.COMPUTED_CATEGORY]: {},
  [DEALTABLE_COLUMNS.COMPUTED_FAMILY]: {},
  [DEALTABLE_COLUMNS.DEFAULT_QUANTITY]: {},
  [DEALTABLE_COLUMNS.DISCOUNTED_PRICE]: {},
  [DEALTABLE_COLUMNS.DISCOUNT_AMT]: {},
  [DEALTABLE_COLUMNS.DISC_PERCENT]: {},
  [DEALTABLE_COLUMNS.DT_GROUP_ID]: {},
  [DEALTABLE_COLUMNS.END_DATE]: {},
  [DEALTABLE_COLUMNS.EXT_BASE_PRICE]: {},
  [DEALTABLE_COLUMNS.IS_QTY_EDITABLE]: {},
  [DEALTABLE_COLUMNS.IS_RENEWABLE]: {},
  [DEALTABLE_COLUMNS.IS_SELLINGTERM_EDITABLE]: {},
  [DEALTABLE_COLUMNS.MAX_USAGE]: {},
  [DEALTABLE_COLUMNS.MAX_USAGE_UOM]: {},
  [DEALTABLE_COLUMNS.MIN_QTY]: {},
  [DEALTABLE_COLUMNS.MIN_SELLING_TERM]: {},
  [DEALTABLE_COLUMNS.NET_PRICE]: {},
  [DEALTABLE_COLUMNS.OVERALL_DISCOUNT_PERCENT]: {},
  [DEALTABLE_COLUMNS.PRICELIST_ITEM]: {},
  [DEALTABLE_COLUMNS.PRICE]: {},
  [DEALTABLE_COLUMNS.PRODUCT]: {},
  [DEALTABLE_COLUMNS.PRODUCT_CODE]: {},
  [DEALTABLE_COLUMNS.PRODUCT_DESC]: {},
  [DEALTABLE_COLUMNS.PRODUCT_NAME]: {},
  [DEALTABLE_COLUMNS.PTYPE]: {},
  [DEALTABLE_COLUMNS.QUANTITY]: {},
  [DEALTABLE_COLUMNS.QUANTITY_UOM]: {},
  [DEALTABLE_COLUMNS.RECURRING_PERIOD]: {},
  [DEALTABLE_COLUMNS.REMARKS]: {},
  [DEALTABLE_COLUMNS.SELLING_TERM]: {},
  [DEALTABLE_COLUMNS.SKU_QUANTITY]: {},
  [DEALTABLE_COLUMNS.START_DATE]: {},
  [DEALTABLE_COLUMNS.UOM]: {},
 */



// WEBPACK FOOTER //
// ./src/mashupV4/constants/index.js